import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, RefreshCw, ArrowLeft, Copy, Download } from "lucide-react";
import QRCode from "qrcode";
import Sidebar from "./Sidebar";
import { Helmet } from "react-helmet";
import FileUploadSection from "./FileUploadSection";
import SEOHelmet from "../../../components/SEOHelmet";

export default function FileToQR() {
  const [files, setFiles] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [qrContent, setQrContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [qrColor, setQrColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [qrSize, setQrSize] = useState(300);
  const [history, setHistory] = useState([]);
  const [isPremium, setIsPremium] = useState(false); // toggle for premium users

  const [toastMessage, setToastMessage] = useState("");

  const tokenClient = useRef(null);


  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const supportedFormats = [
    "pdf","docx","txt","xlsx","csv","pptx",
    "jpg","jpeg","png","gif","zip","rar",
    "mp3","mp4"
  ];

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("fileQrHistory");
    if (saved) setHistory(JSON.parse(saved));
  }, []);

  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem("fileQrHistory", JSON.stringify(history));
  }, [history]);

  useEffect(() => {
  const savedToken = localStorage.getItem("googleAccessToken");
  if (savedToken) {
    setAccessToken(savedToken);
    // fetchUserProfile(savedToken); // Fetch user name/photo
  }
}, []);


  // Initialize OAuth2 token client
//   let tokenClient;
//   const initTokenClient = () => {
//     tokenClient = google.accounts.oauth2.initTokenClient({
//       client_id: "384426887203-97s5ajg8t5o629cgjf98sgt67hgbimqk.apps.googleusercontent.com",
//       scope: "https://www.googleapis.com/auth/drive.file",
//       callback: (tokenResponse) => {setAccessToken(tokenResponse.access_token);
//             localStorage.setItem("googleAccessToken", tokenResponse.access_token);
//       }

//     });
//   };
  

//   let tokenClient;



useEffect(() => {
  if (window.google) {
    tokenClient.current = window.google.accounts.oauth2.initTokenClient({
      client_id: "384426887203-97s5ajg8t5o629cgjf98sgt67hgbimqk.apps.googleusercontent.com",
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: (tokenResponse) => {
        setAccessToken(tokenResponse.access_token);
        localStorage.setItem("googleAccessToken", tokenResponse.access_token);
        // Optionally fetch profile here
      },
    });
  }
}, []);


const handleSignIn = () => {
    if (!tokenClient.current) return;
    tokenClient.current.requestAccessToken();
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!accessToken) {
      alert("Sign in first to upload files to Google Drive.");
      return;
    }

    // Free users can upload only 1 file at a time
    if (!isPremium && selectedFiles.length > 1) {
      alert("Batch upload is a Premium feature.");
      return;
    }

    setFiles(selectedFiles);
    setLoading(true);

    try {
      for (let file of selectedFiles) {
        const extension = file.name.split(".").pop().toLowerCase();
        if (!supportedFormats.includes(extension)) continue;

        // Upload to Google Drive
        const metadata = { name: file.name, mimeType: file.type };
        const formData = new FormData();
        formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
        formData.append("file", file);

        const uploadRes = await fetch(
          "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id",
          { method: "POST", headers: { Authorization: `Bearer ${accessToken}` }, body: formData }
        );
        const uploadData = await uploadRes.json();

        await fetch(`https://www.googleapis.com/drive/v3/files/${uploadData.id}/permissions`, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ role: "reader", type: "anyone" })
        });

        const shareableLink = `https://drive.google.com/uc?id=${uploadData.id}&export=download`;
        setFileUrl(shareableLink);

        // Generate QR
        const qrDataUrl = await QRCode.toDataURL(shareableLink, { 
          width: qrSize, 
          color: { dark: qrColor, light: bgColor } 
        });
        setQrContent(qrDataUrl);

        // Update history
        setHistory(prev => [{ name: file.name, link: shareableLink, qr: qrDataUrl }, ...prev]);
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }
    setLoading(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fileUrl);
    alert("Link copied!");
  };

  const downloadQR = (type = "png") => {
    if (type !== "png" && !isPremium) {
      alert(`${type.toUpperCase()} download is a Premium feature.`);
      return;
    }
    const link = document.createElement("a");
    link.href = qrContent;
    link.download = `qr_code.${type}`;
    link.click();
  };

const handleAction = (message, callback, downloadUrl) => {
  if (callback) callback();
  if (downloadUrl) {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileUrl;
    link.click();
  }
  setToastMessage(message);
  setTimeout(() => setToastMessage(""), 2000); // toast disappears
};



  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 md:ml-64">
<SEOHelmet
  title="File to QR Code Generator"
  description="Convert your files into QR codes instantly with our free File to QR generator. Share documents, images, PDFs, and more via QR code easily."
  keywords="file to QR, convert file to QR code, document QR code, PDF QR code, file QR generator, share files via QR"
  url="https://omkarmore.com/filetoqr"
  image="https://omkarmore.com/LOGO_512.png"
/>

        <div className="sticky top-0 z-30 bg-gray-50 py-4 px-6 shadow-sm">
          <button onClick={() => navigate("/tools")} className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition">
            <ArrowLeft className="w-5 h-5" /> Back to Tools
          </button>
        </div>

        <main className="flex-1 p-6 md:p-10">
          <div className="w-full max-w-xl bg-white shadow-lg rounded-2xl p-6 mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">File to QR Code Generator</h1>
            <p className="text-center text-gray-600 mb-6 text-sm">
              Sign in with Google, upload files, customize QR, and generate QR codes.
            </p>

<FileUploadSection
  accessToken={accessToken}
  handleSignIn={handleSignIn}
  handleFileUpload={handleFileUpload}
  files={files}
  supportedFormats={supportedFormats}
  isPremium={isPremium}
  setAccessToken={setAccessToken}
/>


            {/* QR Customization */}
{fileUrl && (
  <div className="mt-6 bg-gray-50 p-4 rounded-xl shadow-sm border border-gray-200">
    <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">Customize Your QR Code</h3>
    <p className="text-sm text-gray-600 mb-4 text-center">
      Adjust the QR code appearance to match your brand or preference. Premium users can fully customize color, background, and size.
    </p>

    <div className="flex flex-col md:flex-row items-center justify-center gap-4">
      {/* QR Foreground Color */}
      <div className="flex flex-col items-center">
        <label className="text-sm text-gray-700 mb-1">QR Color</label>
        <input
          type="color"
          value={qrColor}
          onChange={e => isPremium ? setQrColor(e.target.value) : alert("Custom QR color is Premium")}
          className={`w-16 h-10 cursor-pointer border rounded ${!isPremium ? "opacity-50 pointer-events-none" : ""}`}
          title="Change QR code color (Premium feature)"
        />
      </div>

      {/* QR Background Color */}
      <div className="flex flex-col items-center">
        <label className="text-sm text-gray-700 mb-1">Background Color</label>
        <input
          type="color"
          value={bgColor}
          onChange={e => isPremium ? setBgColor(e.target.value) : alert("Background color customization is Premium")}
          className={`w-16 h-10 cursor-pointer border rounded ${!isPremium ? "opacity-50 pointer-events-none" : ""}`}
          title="Change QR background color (Premium feature)"
        />
      </div>

      {/* QR Size */}
      <div className="flex flex-col items-center">
        <label className="text-sm text-gray-700 mb-1">QR Size</label>
        <input
          type="number"
          value={qrSize}
          onChange={e => isPremium ? setQrSize(Number(e.target.value)) : alert("Custom QR size is Premium")}
          className={`w-24 p-1 border rounded text-center ${!isPremium ? "opacity-50 pointer-events-none" : ""}`}
          min={100}
          max={1000}
          title="Set QR size in pixels (Premium feature)"
        />
      </div>
    </div>

    {!isPremium && (
      <p className="text-center text-xs text-yellow-700 mt-3">
        Unlock full customization with <span className="font-semibold">Premium</span>.
      </p>
    )}
  </div>
)}

            {/* QR Preview */}
{loading ? (
  <div className="mt-6 flex justify-center text-cyan-600">
    <RefreshCw className="animate-spin h-6 w-6" />
  </div>
) : (
  qrContent && (
    <div className="mt-8 text-center">
      <p className="text-sm text-gray-700 mb-3">
        Scan QR Code to access your file:
      </p>
      <img
        src={qrContent}
        alt="QR Code"
        className="mx-auto border p-2 rounded-lg shadow-sm"
      />

      <div className="mt-4 flex flex-wrap justify-center gap-3 relative">
        {/* Toast notification */}
        {toastMessage && (
          <div className="absolute -top-10 px-4 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-lg animate-fade-in">
            {toastMessage}
          </div>
        )}

        {/* Copy Link */}
        <button
          onClick={() => handleAction("Link copied!", () => navigator.clipboard.writeText(fileUrl))}
          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg shadow-sm transition text-sm font-medium"
        >
          <Copy className="w-4 h-4 text-gray-700" /> Copy Link
        </button>

        {/* Download PNG */}
        <button
          onClick={() => handleAction("PNG downloaded!", () => downloadQR("png"))}
          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg shadow-sm transition text-sm font-medium"
        >
          <Download className="w-4 h-4 text-gray-700" /> Download PNG
        </button>

        {/* Download SVG (Premium) */}
        <button
          onClick={() =>
            isPremium
              ? handleAction("SVG downloaded!", () => downloadQR("svg"))
              : handleAction("SVG download is a Premium feature!")
          }
          className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm text-sm font-medium transition ${
            isPremium
              ? "bg-gray-100 hover:bg-gray-200 text-gray-700"
              : "bg-gray-50 text-gray-400 cursor-not-allowed"
          }`}
        >
          <Download className="w-4 h-4" /> Download SVG
        </button>

        {/* Download File */}
        <button
          onClick={() => handleAction("File downloaded!", null, fileUrl)}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg shadow-sm transition text-sm font-medium"
        >
          <Download className="w-4 h-4" /> Download File
        </button>
      </div>
    </div>
  )
)}



            {/* History */}
            {history.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-semibold mb-2 text-gray-800">Recent Files</h2>
                <ul className="space-y-2">
                  {history.map((h, idx) => (
                    <li key={idx} className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <div className="flex items-center gap-2">
                        {h.qr && <img src={h.qr} alt="QR" className="w-12 h-12"/>}
                        <span className="text-gray-700">{h.name}</span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={()=>{navigator.clipboard.writeText(h.link); alert("Link copied!")}} className="bg-gray-200 px-2 py-1 rounded hover:bg-gray-300">Copy</button>
                        <a href={h.link} download className="bg-cyan-600 text-white px-3 py-1 rounded hover:bg-cyan-700">Download</a>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Premium Features Info */}
            {!isPremium && (
              <div className="mt-10 p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-2 text-yellow-800">Premium Features</h2>
                <p className="text-yellow-700 mb-4 text-sm">
                  Upgrade to unlock advanced QR features:
                </p>
                <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                  <li>Batch file upload</li>
                  <li>SVG / PDF QR download</li>
                  <li>Custom QR logo / watermark</li>
                  <li>Gradient QR codes</li>
                  <li>Analytics for QR scans</li>
                  <li>Password-protected file links</li>
                </ul>
                <div className="mt-4 flex justify-center">
                  <button onClick={()=>alert("Redirect to payment gateway")} className="bg-yellow-600 text-white px-5 py-2 rounded-lg shadow hover:bg-yellow-700 transition">
                    Upgrade to Premium
                  </button>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
